/** @jsx jsx */
/** @jsxFrag Fragment */
import { jsx } from '@emotion/react';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { Button, Grid, Typography2 } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsUpFilledIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/program-skills-objectives';

type FeedbackBannerState = 'show' | 'thanks' | 'hidden';

type Props = {
  onFeedbackGiven: (prompt: string, feedback: string) => void;
};

const LevelSetsFeedbackBanner = ({ onFeedbackGiven }: Props) => {
  const [state, setState] = useState<FeedbackBannerState>('show');
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    },
    []
  );

  const closeDialog = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async (feedback: string) => {
      onFeedbackGiven('Do you agree with your score?', feedback);
      setState('thanks');
      timeoutIdRef.current = setTimeout(() => {
        timeoutIdRef.current = null;
        setState('hidden');
      }, 3000);
    },
    [onFeedbackGiven]
  );

  if (state === 'hidden') return null;

  return (
    <Grid
      alignItems="center"
      spacing={24}
      container
      css={{
        background: 'var(--cds-color-grey-25)',
        marginBottom: 'var(--cds-spacing-400)',
        padding: 'var(--cds-spacing-50)',
      }}
    >
      {state === 'thanks' && (
        <Grid item xs={12}>
          <Typography2 role="alert" aria-live="assertive" variant="subtitleMedium" component="p">
            {_t('Thanks for your feedback!')}
          </Typography2>
        </Grid>
      )}
      {state === 'show' && (
        <>
          <Grid item xs={12} sm="auto">
            <Typography2 role="presentation" variant="subtitleMedium" component="p">
              {_t('Do you agree with your score?')}
            </Typography2>
          </Grid>
          <Grid item xs={12} sm="auto" css={{ marginLeft: 'auto' }}>
            <Button
              onClick={() => closeDialog('yes')}
              fullWidth={true}
              size="small"
              variant="secondary"
              icon={<ThumbsUpFilledIcon />}
              iconPosition="after"
              aria-label={_t('Do you agree with your score? Yes')}
            >
              {_t('Yes')}
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button
              onClick={() => closeDialog('no')}
              fullWidth={true}
              size="small"
              variant="secondary"
              icon={<ThumbsDownFilledIcon />}
              iconPosition="after"
              aria-label={_t('Do you agree with your score? No')}
            >
              {_t('No')}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default LevelSetsFeedbackBanner;
